import React, { useState } from 'react';
import { blockNonNumericKeys, getNumericFieldError } from '@/utils/numericValidation';
import { NumericField } from '@/components/NumericField';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';

export const CWMetalTypesSection = ({
  // Data
  metalPanelTypes,
  selectedMetalPanelTypeIds,
  newMetalType,
  editingMetalType,
  showMetalTypeDialog,
  operationLoading,
  // Permissions
  canEditCSIItems,
  canManageProject,
  // Handlers
  setNewMetalType,
  setShowMetalTypeDialog,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateMetalType,
  handleEditMetalType,
  handleDeleteMetalType,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const thicknessRules = { required: true, min: 0, allowFraction: true, label: 'Thickness' };
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (getNumericFieldError(newMetalType.thickness_mm, thicknessRules)) {
      e.preventDefault();
      return;
    }
    handleCreateMetalType(e);
  };

  return (
    <TabsContent value="cw-metal-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Metal Panel Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && metalPanelTypes.length > 0 && (
            <>
              <Button variant="outline" onClick={() => selectAllItems('metalPanelType')}>
                {selectedMetalPanelTypeIds.length === metalPanelTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('metalPanelType')}
                disabled={selectedMetalPanelTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedMetalPanelTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showMetalTypeDialog} onOpenChange={(open) => { setSubmitAttempted(false); setShowMetalTypeDialog(open); }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Metal Type
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingMetalType ? 'Edit Metal Panel Type' : 'Create Metal Panel Type'}</DialogTitle>
                <DialogDescription>Define metal panel specifications</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., MT1, MT2) *</Label>
                    <Input value={newMetalType.name} onChange={(e) => setNewMetalType({...newMetalType, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newMetalType.manufacturer} onChange={(e) => setNewMetalType({...newMetalType, manufacturer: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newMetalType.model} onChange={(e) => setNewMetalType({...newMetalType, model: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Thickness (in) *</Label>
                    <NumericField value={newMetalType.thickness_mm} onChange={(v) => setNewMetalType({...newMetalType, thickness_mm: v})} rules={thicknessRules} showError={submitAttempted} placeholder="e.g., 0.125" required data-testid="cw-metal-thickness-input" errorTestId="cw-metal-thickness-error" />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Color Code (HEX/RAL)</Label>
                    <Input value={newMetalType.colorCode} onChange={(e) => setNewMetalType({...newMetalType, colorCode: e.target.value})} placeholder="#FFFFFF or RAL 9016" />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={operationLoading}>
                  {operationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingMetalType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingMetalType ? 'Update Metal Type' : 'Create Metal Type'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {metalPanelTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No metal panel types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedMetalPanelTypeIds.length === metalPanelTypes.length && metalPanelTypes.length > 0}
                      onCheckedChange={() => selectAllItems('metalPanelType')}
                    />
                  </th>
                )}
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Thickness</th>
                <th className="w-28 px-3 py-3 text-left font-semibold text-slate-700">Color Code</th>
                {canEditCSIItems && (
                  <th className="w-20 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {metalPanelTypes.map((metal, idx) => (
                <tr 
                  key={metal.id}
                  className={`border-b hover:bg-slate-50 transition-colors ${
                    selectedMetalPanelTypeIds.includes(metal.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                  }`}
                >
                  {canEditCSIItems && (
                    <td className="w-12 px-3 py-2">
                      <Checkbox
                        checked={selectedMetalPanelTypeIds.includes(metal.id)}
                        onCheckedChange={(checked) => toggleItemSelection(metal.id, 'metalPanelType', { target: { checked } })}
                      />
                    </td>
                  )}
                  <td className="w-20 px-3 py-2 font-medium text-slate-900">{metal.name}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.manufacturer || '-'}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.model || '-'}</td>
                  <td className="w-24 px-3 py-2 text-slate-600">{metal.thickness_mm ? `${metal.thickness_mm}"` : '-'}</td>
                  <td className="w-28 px-3 py-2 text-slate-600">{metal.colorCode || '-'}</td>
                  {canEditCSIItems && (
                    <td className="w-20 px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditMetalType(metal)}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => handleDeleteMetalType(metal.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TabsContent>
  );
};

export default CWMetalTypesSection;
